import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Link, C1StyledLink } from 'components/core/links/C1Link'
import { ExternalLink } from 'components/core/links/external-link'
import { unsafeRoute } from 'lib/routes'
import dynamic from 'next/dynamic'
import { FC, ReactElement, ReactNode, useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { FormattedMessage } from 'react-intl'
import type { Options } from 'react-markdown'

const validateUri = (uri: string): string => {
    try {
        const allowedProtocols = ['https:', 'mailto:']
        const parsedUrl = new URL(uri, window.location.origin)

        return allowedProtocols.includes(parsedUrl.protocol) ? parsedUrl.toString() : ''
    } catch (e) {
        return '' // Discard malformed or unsafe URIs
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createMdToMuiComponents = (darkMode?: boolean) => ({
    p: ({ children }: { children?: ReactNode }): ReactElement => <Typography sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    h1: ({ children }: { children?: ReactNode }): ReactElement => <Typography variant="h1" component="div" mb={4} sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    h2: ({ children }: { children?: ReactNode }): ReactElement => <Typography variant="h2" component="div" my={4} sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    h3: ({ children }: { children?: ReactNode }): ReactElement => <Typography variant="h3" component="div" my={3} sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    h4: ({ children }: { children?: ReactNode }): ReactElement => <Typography variant="h4" component="div" my={2} sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    h5: ({ children }: { children?: ReactNode }): ReactElement => <Typography variant="h5" component="div" my={2} sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    h6: ({ children }: { children?: ReactNode }): ReactElement => <Typography variant="h6" component="div" my={2} sx={{ lineHeight: 1.6 }}>{children}</Typography>,
    a: ({ href, children }: React.ComponentProps<'a'>): ReactElement => {
        if (href === undefined) {
            return <Typography component="span" sx={{ lineHeight: 1.6 }}>{children}</Typography>
        }

        const linkSx = darkMode ? { color: 'primary.light' } : undefined

        try {
            const isExternal = new URL(href).hostname !== window.location.hostname
            if (isExternal) {
                return <ExternalLink href={href} sx={linkSx}>{children}</ExternalLink>
            }
            return darkMode
                ? <C1StyledLink href={unsafeRoute(href)} sx={linkSx}>{children}</C1StyledLink>
                : <C1Link href={unsafeRoute(href)}>{children}</C1Link>
        } catch (e) {
            return <Typography component="span" sx={{ lineHeight: 1.6 }}>{children}</Typography>
        }
    },
    hr: (): ReactElement => <Divider sx={{ my: 2 }} />,
    ul: ({ children }: { children?: ReactNode }): ReactElement => (
        <Box
            component="ul"
            sx={{
                lineHeight: 1.6,
                paddingLeft: 2,
                '& li': {
                    paddingLeft: 1,
                    marginBottom: 1,
                },
            }}
        >
            {children}
        </Box>
    ),
    ol: ({ children }: { children?: ReactNode }): ReactElement => (
        <Box
            component="ol"
            sx={{
                lineHeight: 1.6,
                paddingLeft: 2,
                '& li': {
                    paddingLeft: 1,
                    marginBottom: 1,
                },
            }}
        >
            {children}
        </Box>
    ),
    li: ({ children }: { children?: ReactNode }): ReactElement => (
        <Typography component="li" sx={{ lineHeight: 1.6 }}>
            {children}
        </Typography>
    ),
    pre: ({ children }: { children?: ReactNode }): ReactElement => (
        <Box
            component="pre"
            sx={{
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                overflowX: 'auto',
                borderRadius: 1,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                p: 2,
            }}
        >
            {children}
        </Box>
    ),
    table: ({ children }: { children?: ReactNode }): ReactElement => (
        <Table sx={{ my: 2, border: '1px solid', borderColor: 'divider' }}>
            {children}
        </Table>
    ),
    thead: ({ children }: { children?: ReactNode }): ReactElement => <TableHead>{children}</TableHead>,
    tbody: ({ children }: { children?: ReactNode }): ReactElement => <TableBody>{children}</TableBody>,
    tr: ({ children }: { children?: ReactNode }): ReactElement => <TableRow>{children}</TableRow>,
    th: ({ children }: { children?: ReactNode }): ReactElement => (
        <TableCell sx={{ fontWeight: 'bold', border: '1px solid', borderColor: 'divider' }}>
            {children}
        </TableCell>
    ),
    td: ({ children }: { children?: ReactNode }): ReactElement => (
        <TableCell sx={{ border: '1px solid', borderColor: 'divider' }}>
            {children}
        </TableCell>
    ),
})

type SafeMarkdownProps = Options & {
    children: ReactNode
    darkMode?: boolean
}

export const SafeMarkdown = dynamic(async () => {
    const [
        { default: ReactMarkdown },
        { default: remarkGfm },
        { default: rehypeRaw },
        { default: rehypeSanitize, defaultSchema },
        { default: rehypeExternalLinks },
    ] = await Promise.all([
        import('react-markdown'),
        import('remark-gfm'),
        import('rehype-raw'),
        import('rehype-sanitize'),
        import('rehype-external-links'),
    ])

    // Customize the default schema to remove img, picture, and source tags
    // These are blocked by CSP anyway, and can be used for tracking
    const sanitizeSchema = {
        ...defaultSchema,
        tagNames: defaultSchema.tagNames?.filter((tag) => !['img', 'picture', 'source'].includes(tag)),
    }

    return ({ children, darkMode, ...props }: SafeMarkdownProps): ReactNode => <ErrorBoundary
        fallbackRender={({ error }): ReactNode => <MinimalAPIError err={<FormattedMessage id="error.unknown" defaultMessage="Error while rendering markdown content: {err}." values={{ err: error.message }} />} />}
    >
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
                rehypeRaw,
                [rehypeSanitize, sanitizeSchema],
                [
                    rehypeExternalLinks,
                    {
                        target: '_blank',
                        rel: ['noopener', 'noreferrer'],
                    },
                ],
            ]}
            urlTransform={validateUri}
            {...props}
            components={createMdToMuiComponents(darkMode)}
        >
            {children}
        </ReactMarkdown>
    </ErrorBoundary>
}, { ssr: false })

type SafeMarkdownWithLineClampProps = {
    text: string
    lineClamp: number
}

export const SafeMarkdownWithLineClamp: FC<SafeMarkdownWithLineClampProps> = ({ text, lineClamp }) => {
    const markdown = useMemo(() => <SafeMarkdown>{text}</SafeMarkdown>, [text])
    const tooltipMarkdown = useMemo(() => <SafeMarkdown darkMode>{text}</SafeMarkdown>, [text])
    return (
        <Tooltip
            title={tooltipMarkdown}
            placement="bottom-start"
        >
            <Box
                sx={{
                    WebkitLineClamp: lineClamp,
                    WebkitBoxOrient: 'vertical',
                    display: '-webkit-box',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {markdown}
            </Box>
        </Tooltip>
    )
}
